import { useEffect, useRef, useState } from 'react';

/**
 * GoldWordmark — the splash's forged-gold "Gilding", compacted for headers.
 *
 * Same renderer as the splash: exact-EDT SDF of Fraunces 900, hand-painted
 * gold matcap, circular-arc bevel, gold-leaf entrance (once per session),
 * travelling sheen, and burnish-under-the-pointer. No dust/mirror/bloom —
 * at header scale the metal itself carries the look, and the header must
 * stay cheap enough to live on every page.
 *
 * Renders a CSS-gilded text placeholder instantly; the canvas fades in over
 * it once WebGL is ready (and remains the permanent fallback if it isn't).
 */
export function GoldWordmark({ className }: { className?: string }) {
  const hostRef = useRef<HTMLDivElement>(null);
  const [live, setLive] = useState(false);

  useEffect(() => {
    let dispose: (() => void) | null = null;
    let dead = false;
    const host = hostRef.current;
    if (!host) return;
    void (async () => {
      try {
        const d = await initWordmark(host);
        if (dead) {
          d();
          return;
        }
        dispose = d;
        setLive(true);
      } catch (e) {
        console.warn('GoldWordmark: WebGL unavailable, keeping static mark', e);
      }
    })();
    return () => {
      dead = true;
      dispose?.();
    };
  }, []);

  return (
    <span
      className={`relative inline-block select-none ${className ?? ''}`}
      role="img"
      aria-label="Gilding"
    >
      {/* instant-paint placeholder & permanent fallback */}
      <span
        aria-hidden="true"
        className="text-gilded font-display text-2xl font-black tracking-tight transition-opacity duration-300"
        style={{ opacity: live ? 0 : 1 }}
      >
        Gilding
      </span>
      <span
        ref={hostRef}
        aria-hidden="true"
        className="absolute inset-0 transition-opacity duration-300"
        style={{ opacity: live ? 1 : 0 }}
      />
    </span>
  );
}

/* ====================================================================== */

const SESSION_KEY = 'gilding_header_leafed';
const SPREAD = 26;
const BIG = 1e20;

async function initWordmark(host: HTMLElement): Promise<() => void> {
  const THREE = await import('three');
  await document.fonts.load('900 130px "Fraunces"');

  /* ---- exact EDT SDF (Felzenszwalb), header-sized ---- */
  function edt1d(
    f: Float64Array,
    d: Float64Array,
    v: Int32Array,
    z: Float64Array,
    n: number,
  ): void {
    let k = 0;
    v[0] = 0;
    z[0] = -BIG;
    z[1] = BIG;
    for (let q = 1; q < n; q++) {
      let s = (f[q]! + q * q - (f[v[k]!]! + v[k]! * v[k]!)) / (2 * q - 2 * v[k]!);
      while (s <= z[k]!) {
        k--;
        s = (f[q]! + q * q - (f[v[k]!]! + v[k]! * v[k]!)) / (2 * q - 2 * v[k]!);
      }
      k++;
      v[k] = q;
      z[k] = s;
      z[k + 1] = BIG;
    }
    k = 0;
    for (let q = 0; q < n; q++) {
      while (z[k + 1]! < q) k++;
      d[q] = (q - v[k]!) * (q - v[k]!) + f[v[k]!]!;
    }
  }
  function edt2d(grid: Float64Array, w: number, h: number): void {
    const m = Math.max(w, h);
    const f = new Float64Array(m),
      d = new Float64Array(m),
      v = new Int32Array(m),
      z = new Float64Array(m + 1);
    for (let x = 0; x < w; x++) {
      for (let y = 0; y < h; y++) f[y] = grid[y * w + x]!;
      edt1d(f, d, v, z, h);
      for (let y = 0; y < h; y++) grid[y * w + x] = d[y]!;
    }
    for (let y = 0; y < h; y++) {
      for (let x = 0; x < w; x++) f[x] = grid[y * w + x]!;
      edt1d(f, d, v, z, w);
      for (let x = 0; x < w; x++) grid[y * w + x] = d[x]!;
    }
  }
  function buildSDF() {
    // The texture hugs the metal (no letterboxing), but the bounds come from
    // the INK, not from font metrics: canvas actualBoundingBox* under-reports
    // Fraunces' ascenders (variable font with an optical-size axis), which
    // shaved the d/l tops flat when trusted directly. Draw on a roomy stage,
    // scan the alpha channel for the true ink box, then crop with even PAD.
    const FONT = '900 240px "Fraunces", serif';
    const probe = document.createElement('canvas').getContext('2d')!;
    probe.font = FONT;
    const m = probe.measureText('Gilding');
    const PAD = SPREAD + 4;
    const W0 = Math.ceil(m.width) + 320;
    const H0 = 560;
    const c0 = document.createElement('canvas');
    c0.width = W0;
    c0.height = H0;
    const g0 = c0.getContext('2d')!;
    g0.fillStyle = '#fff';
    g0.textAlign = 'left';
    g0.textBaseline = 'alphabetic';
    g0.font = FONT;
    g0.fillText('Gilding', 160, 380);
    const raw = g0.getImageData(0, 0, W0, H0).data;
    let minX = W0,
      minY = H0,
      maxX = -1,
      maxY = -1;
    for (let y = 0; y < H0; y++) {
      for (let x = 0; x < W0; x++) {
        if (raw[(y * W0 + x) * 4 + 3]! > 127) {
          if (x < minX) minX = x;
          if (x > maxX) maxX = x;
          if (y < minY) minY = y;
          if (y > maxY) maxY = y;
        }
      }
    }
    if (maxX < 0) {
      minX = 0;
      minY = 0;
      maxX = W0 - 1;
      maxY = H0 - 1;
    }
    const bw = maxX - minX + 1;
    const bh = maxY - minY + 1;
    const TW = bw + PAD * 2;
    const THh = bh + PAD * 2;
    const c = document.createElement('canvas');
    c.width = TW;
    c.height = THh;
    const g = c.getContext('2d')!;
    g.drawImage(c0, minX, minY, bw, bh, PAD, PAD, bw, bh);
    const img = g.getImageData(0, 0, TW, THh).data;
    const n = TW * THh;
    const inside = new Float64Array(n),
      outside = new Float64Array(n);
    for (let i = 0; i < n; i++) {
      const a = img[i * 4 + 3]! > 127;
      inside[i] = a ? BIG : 0;
      outside[i] = a ? 0 : BIG;
    }
    edt2d(inside, TW, THh);
    edt2d(outside, TW, THh);
    const data = new Float32Array(n);
    for (let i = 0; i < n; i++)
      data[i] = (Math.sqrt(outside[i]!) - Math.sqrt(inside[i]!)) / SPREAD;
    const tex = new THREE.DataTexture(data, TW, THh, THREE.RedFormat, THREE.FloatType);
    tex.minFilter = tex.magFilter = THREE.LinearFilter;
    tex.flipY = true;
    tex.needsUpdate = true;
    return { tex, w: TW, h: THh };
  }

  /* ---- painted matcap (identical recipe to the splash) ---- */
  function paintMatcap() {
    const S = 256;
    const c = document.createElement('canvas');
    c.width = c.height = S;
    const g = c.getContext('2d')!;
    const cx = S / 2,
      cy = S / 2,
      R = S / 2;
    const base = g.createLinearGradient(0, 0, 0, S);
    base.addColorStop(0, '#7d521a');
    base.addColorStop(0.4, '#c2922f');
    base.addColorStop(0.6, '#5d3f10');
    base.addColorStop(1, '#1a0f02');
    g.fillStyle = base;
    g.beginPath();
    g.arc(cx, cy, R, 0, 7);
    g.fill();
    const lg = (x: number, y: number, r: number, stops: [number, string][]) => {
      const G = g.createRadialGradient(x, y, 0, x, y, r);
      stops.forEach((s) => G.addColorStop(s[0], s[1]));
      g.fillStyle = G;
      g.beginPath();
      g.arc(cx, cy, R, 0, 7);
      g.fill();
    };
    lg(S * 0.34, S * 0.26, S * 0.55, [
      [0, 'rgba(255,250,232,.97)'],
      [0.26, 'rgba(255,226,160,.5)'],
      [1, 'rgba(255,226,160,0)'],
    ]);
    lg(S * 0.36, S * 0.24, S * 0.1, [
      [0, 'rgba(255,255,255,1)'],
      [1, 'rgba(255,255,255,0)'],
    ]);
    g.save();
    g.globalCompositeOperation = 'lighter';
    const G2 = g.createLinearGradient(S * 0.78, 0, S, 0);
    G2.addColorStop(0, 'rgba(120,140,170,0)');
    G2.addColorStop(0.7, 'rgba(190,210,235,.36)');
    G2.addColorStop(1, 'rgba(235,242,255,.66)');
    g.fillStyle = G2;
    g.beginPath();
    g.arc(cx, cy, R, -0.9, 0.9);
    g.lineTo(cx, cy);
    g.fill();
    g.restore();
    lg(S * 0.5, S * 1.02, S * 0.5, [
      [0, 'rgba(255,150,50,.5)'],
      [1, 'rgba(255,150,50,0)'],
    ]);
    g.save();
    g.globalCompositeOperation = 'multiply';
    const G3 = g.createLinearGradient(0, S * 0.5, 0, S * 0.82);
    G3.addColorStop(0, 'rgba(255,255,255,1)');
    G3.addColorStop(0.45, 'rgba(60,38,12,1)');
    G3.addColorStop(1, 'rgba(255,255,255,1)');
    g.fillStyle = G3;
    g.beginPath();
    g.arc(cx, cy, R, 0, 7);
    g.fill();
    g.restore();
    const t = new THREE.CanvasTexture(c);
    t.colorSpace = THREE.SRGBColorSpace;
    return t;
  }

  /* ---- renderer on an orthographic stage that IS the word ---- */
  const sdf = buildSDF();
  const aspect = sdf.w / sdf.h;
  // Size the lockup from the word's true aspect: height comes from the
  // caller's class, width follows the metal exactly.
  const outer = host.parentElement!;
  const hpx = outer.clientHeight || 44;
  outer.style.width = `${Math.round(hpx * aspect)}px`;
  const renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true });
  // Floor of 3: 1x desktop monitors otherwise get a 1x backing store that
  // turns to mush under browser zoom. The canvas is header-sized; the cost
  // of always rendering dense is trivial.
  renderer.setPixelRatio(Math.max(3, window.devicePixelRatio || 1));
  renderer.setSize(host.clientWidth || hpx * aspect, host.clientHeight || hpx);
  renderer.toneMapping = THREE.ACESFilmicToneMapping;
  renderer.domElement.style.position = 'absolute';
  renderer.domElement.style.inset = '0';
  host.appendChild(renderer.domElement);
  const scene = new THREE.Scene();
  const camera = new THREE.OrthographicCamera(-1, 1, 1, -1, 0.1, 10);
  camera.position.z = 1;

  const TRAIL = 8;
  const alreadyLeafed = (() => {
    try {
      return sessionStorage.getItem(SESSION_KEY) === '1';
    } catch {
      return false;
    }
  })();
  const uniforms = {
    uSDF: { value: sdf.tex },
    uMat: { value: paintMatcap() },
    uTime: { value: 0 },
    uApply: { value: alreadyLeafed ? 1.6 : 0 },
    uAspect: { value: aspect },
    uTexel: { value: new THREE.Vector2(1 / sdf.w, 1 / sdf.h) },
    uTrail: {
      value: Array.from({ length: TRAIL }, () => new THREE.Vector4(0, 0, 99, 0)),
    },
  };
  const frag = /* glsl */ `
precision highp float;
varying vec2 vUv;
uniform sampler2D uSDF,uMat;
uniform float uTime,uApply,uAspect;
uniform vec2 uTexel;
uniform vec4 uTrail[${TRAIL}];
float hash(vec2 p){return fract(sin(dot(p,vec2(127.1,311.7)))*43758.5453);}
vec2 hash2(vec2 p){return vec2(hash(p),hash(p+19.7));}
vec2 grad(vec2 i){float h=hash(i)*6.2831853;return vec2(cos(h),sin(h));}
float gnoise(vec2 p){
  vec2 i=floor(p),f=fract(p);
  vec2 u=f*f*f*(f*(f*6.-15.)+10.);
  return mix(mix(dot(grad(i),f),dot(grad(i+vec2(1,0)),f-vec2(1,0)),u.x),
             mix(dot(grad(i+vec2(0,1)),f-vec2(0,1)),dot(grad(i+vec2(1,1)),f-vec2(1,1)),u.x),u.y);
}
const mat2 ROT=mat2(.8,-.6,.6,.8);
float fbm(vec2 p){float v=0.,a=.5;for(int i=0;i<4;i++){v+=a*gnoise(p);p=ROT*p*2.02;a*=.5;}return v;}
float sdf(vec2 uv){return texture2D(uSDF,uv).r;}
const float LEAF=0.5;
const mat2 LR=mat2(0.993,-0.119,0.119,0.993);
vec2 leafCell(vec2 p){return floor((LR*p)/LEAF);}
float leafSettle(vec2 cell){
  float d0=(cell.x*LEAF/uAspect)*0.5+(1.0-cell.y*LEAF)*0.1+hash(cell)*0.25;
  return smoothstep(0.0,1.0,clamp((uApply-d0)/0.22,0.,1.));
}
float burnish(vec2 p){
  float b=0.;
  for(int i=0;i<${TRAIL};i++){
    vec2 tp=vec2(uTrail[i].x*uAspect,uTrail[i].y);
    float age=uTrail[i].z,amp=uTrail[i].w;
    float r2=dot(p-tp,p-tp);
    b+=amp*exp(-r2*40.0)*exp(-age*2.6);
  }
  return clamp(b,0.,1.2);
}
float heightAt(vec2 uv,float d){
  float x=clamp(-d/0.34,0.,1.);
  float plate=sqrt(x*(2.0-x));
  vec2 p=vec2(uv.x*uAspect,uv.y);
  float foil=fbm(p*6.0)*0.012+gnoise(p*40.0)*0.003;
  float face=smoothstep(0.78,0.985,plate);
  return plate+foil*face;
}
void main(){
  vec2 p=vec2(vUv.x*uAspect,vUv.y);
  float d=sdf(vUv);
  float settle=leafSettle(leafCell(p));
  float aa=max(fwidth(d),0.006)*1.4;
  float a=smoothstep(aa,-aa,d)*settle;
  if(a<0.003) discard;
  vec2 e=uTexel*1.6;
  float hx=heightAt(vUv+vec2(e.x,0.),sdf(vUv+vec2(e.x,0.)))-heightAt(vUv-vec2(e.x,0.),sdf(vUv-vec2(e.x,0.)));
  float hy=heightAt(vUv+vec2(0.,e.y),sdf(vUv+vec2(0.,e.y)))-heightAt(vUv-vec2(0.,e.y),sdf(vUv-vec2(0.,e.y)));
  vec3 n=normalize(vec3(-hx*8.0,-hy*8.0,1.0));
  vec2 tiltJ=(hash2(leafCell(p))-0.5)*1.4*(1.0-settle);
  n=normalize(vec3(n.xy+tiltJ,n.z));
  float B=burnish(p);
  n=normalize(mix(n,vec3(0.,0.,1.),clamp(B*0.55,0.,0.6)));
  vec3 mc=texture2D(uMat,n.xy*0.5+0.5).rgb;
  vec3 tint=mix(vec3(.55,.36,.12),vec3(1.10,.96,.72),clamp(n.y*.5+.62,0.,1.));
  vec3 col=mc*tint*1.18+pow(mc.r,6.0)*vec3(1.,.95,.8)*.6;
  /* travelling sheen — same living light as the splash */
  float sweep=fract(uTime*0.14);
  col+=vec3(1.,.97,.85)*pow(max(0.,1.0-abs(vUv.x-sweep)*6.0),3.0)*0.35;
  col+=vec3(1.0,.9,.6)*settle*(1.0-settle)*3.0;
  col+=vec3(1.,.93,.72)*B*.32;
  col*=1.0+B*.18;
  gl_FragColor=vec4(col,a);
}`;
  const vert =
    'varying vec2 vUv;void main(){vUv=uv;gl_Position=projectionMatrix*modelViewMatrix*vec4(position,1.);}';
  const mesh = new THREE.Mesh(
    new THREE.PlaneGeometry(2, 2),
    new THREE.ShaderMaterial({
      uniforms,
      vertexShader: vert,
      fragmentShader: frag,
      transparent: true,
    }),
  );
  scene.add(mesh);

  /* burnish under the pointer (header-local) */
  let ti = 0,
    lastT = 0;
  const addTrail = (x: number, y: number, amp: number): void => {
    uniforms.uTrail.value[ti++ % TRAIL]!.set(x, y, 0, amp);
  };
  const toUv = (e: PointerEvent): { x: number; y: number } | null => {
    const r = renderer.domElement.getBoundingClientRect();
    const x = (e.clientX - r.left) / r.width;
    const y = 1 - (e.clientY - r.top) / r.height;
    return x >= 0 && x <= 1 && y >= 0 && y <= 1 ? { x, y } : null;
  };
  const onMove = (e: PointerEvent): void => {
    const uv = toUv(e);
    const now = performance.now();
    if (uv && now - lastT > 30) {
      lastT = now;
      addTrail(uv.x, uv.y, 0.6);
    }
  };
  const onDown = (e: PointerEvent): void => {
    const uv = toUv(e);
    if (uv) addTrail(uv.x, uv.y, 1.6);
  };
  host.addEventListener('pointermove', onMove);
  host.addEventListener('pointerdown', onDown);

  const onResize = (): void => {
    const h2 = outer.clientHeight || hpx;
    outer.style.width = `${Math.round(h2 * aspect)}px`;
    // Browser zoom changes devicePixelRatio and fires resize — track it so
    // a zoomed-in header re-renders dense instead of upscaling stale pixels.
    renderer.setPixelRatio(Math.max(3, window.devicePixelRatio || 1));
    renderer.setSize(host.clientWidth || h2 * aspect, host.clientHeight || h2);
  };
  window.addEventListener('resize', onResize);

  const T0 = performance.now() / 1000;
  let raf = 0;
  let flagged = alreadyLeafed;
  function loop(): void {
    raf = requestAnimationFrame(loop);
    const now = performance.now() / 1000;
    uniforms.uTime.value = now;
    if (!alreadyLeafed) {
      uniforms.uApply.value = Math.min(1.6, (now - T0) / 1.0);
      if (!flagged && uniforms.uApply.value >= 1.6) {
        flagged = true;
        try {
          sessionStorage.setItem(SESSION_KEY, '1');
        } catch {
          /* fine */
        }
      }
    }
    uniforms.uTrail.value.forEach((r) => (r.z += 0.016));
    renderer.render(scene, camera);
  }
  loop();

  return () => {
    cancelAnimationFrame(raf);
    host.removeEventListener('pointermove', onMove);
    host.removeEventListener('pointerdown', onDown);
    window.removeEventListener('resize', onResize);
    renderer.dispose();
    renderer.domElement.remove();
  };
}
